export interface GpmsQualityIndicatorRecordLike {
  domain?: string | null;
  numerator?: number | string | null;
  denominator?: number | string | null;
  rate?: number | string | null;
}

export interface GpmsSubmissionLike {
  id?: string | null;
  facilityId?: string | null;
  facilityName?: string | null;
  status?: string | null;
  totalBedDays?: number | string | null;
  averageOccupancy?: number | string | null;
  submittedAt?: string | null;
  aihwReference?: string | null;
  qualityIndicatorRecords?: GpmsQualityIndicatorRecordLike[] | null;
}

export interface GpmsSubmissionValidation {
  id: string | null;
  facilityId: string | null;
  facilityName: string;
  status: string;
  missingDomains: string[];
  zeroDenominatorDomains: string[];
  negativeValueDomains: string[];
  missingBedDays: boolean;
  missingAihwReference: boolean;
  missingSubmittedAt: boolean;
  blockerCount: number;
  blockers: string[];
  // Canonical lifecycle aligned with DB CHECK and AIHW QI Program Manual 4.0:
  //   DRAFT -> READY -> SUBMITTED -> ACCEPTED | ACKNOWLEDGED | REJECTED -> CLOSED
  readiness:
    | 'blocked'
    | 'draft'
    | 'ready'
    | 'submitted'
    | 'accepted'
    | 'acknowledged'
    | 'rejected'
    | 'closed';
}

export interface GpmsWorkflowSummary {
  totalSubmissions: number;
  blockedCount: number;
  draftCount: number;
  readyCount: number;
  submittedCount: number;
  acceptedCount: number;
  acknowledgedCount: number;
  rejectedCount: number;
  closedCount: number;
  missingDomainCount: number;
  missingReferenceCount: number;
  validations: GpmsSubmissionValidation[];
  canGenerateExport: boolean;
}

function asFiniteNumber(value: number | string | null | undefined) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// Submitted-side states: the row has been sent to AIHW. AIHW reference + the
// submitted_at timestamp must be present once the row reaches any of these.
const SUBMITTED_SIDE_STATES = new Set([
  'SUBMITTED',
  'ACCEPTED',
  'ACKNOWLEDGED',
  'REJECTED',
  'CLOSED',
]);

function isSubmittedSideStatus(status: string) {
  return SUBMITTED_SIDE_STATES.has(status.toUpperCase());
}

function resolveReadiness(
  status: string,
  blockerCount: number,
): GpmsSubmissionValidation['readiness'] {
  if (blockerCount > 0) return 'blocked';
  const normalized = status.toUpperCase();
  if (normalized === 'CLOSED') return 'closed';
  if (normalized === 'REJECTED') return 'rejected';
  if (normalized === 'ACKNOWLEDGED') return 'acknowledged';
  if (normalized === 'ACCEPTED') return 'accepted';
  if (normalized === 'SUBMITTED') return 'submitted';
  if (normalized === 'READY') return 'ready';
  return 'draft';
}

export function validateGpmsSubmission(
  submission: GpmsSubmissionLike,
  expectedDomains: readonly string[],
): GpmsSubmissionValidation {
  const status = String(submission.status || 'DRAFT').toUpperCase();
  const records = submission.qualityIndicatorRecords || [];
  const recordDomains = new Set(
    records.map((record) => String(record.domain || '').trim()).filter(Boolean),
  );

  const expectedDomainSet = new Set(expectedDomains);
  const missingDomains = expectedDomains.filter((domain) => !recordDomains.has(domain));
  // Zero/negative checks are scoped to the expected (provider-submitted) domains
  // so a QFR-derived or otherwise non-submitted record can never block the GPMS
  // submission — only the domains that actually reach the regulator are gated.
  const expectedRecords = records.filter((record) =>
    expectedDomainSet.has(String(record.domain || '').trim()),
  );
  const zeroDenominatorDomains = expectedRecords
    .filter((record) => {
      const denominator = asFiniteNumber(record.denominator);
      return denominator !== null && denominator <= 0;
    })
    .map((record) => String(record.domain || '').trim())
    .filter(Boolean);

  const negativeValueDomains = expectedRecords
    .filter((record) => {
      const numerator = asFiniteNumber(record.numerator);
      const denominator = asFiniteNumber(record.denominator);
      const rate = asFiniteNumber(record.rate);
      return (
        (numerator !== null && numerator < 0) ||
        (denominator !== null && denominator < 0) ||
        (rate !== null && rate < 0)
      );
    })
    .map((record) => String(record.domain || '').trim())
    .filter(Boolean);

  const totalBedDays = asFiniteNumber(submission.totalBedDays);
  const missingBedDays = totalBedDays === null || totalBedDays <= 0;
  const missingAihwReference =
    isSubmittedSideStatus(status) && !String(submission.aihwReference || '').trim();
  const missingSubmittedAt = isSubmittedSideStatus(status) && !submission.submittedAt;

  const blockers: string[] = [];
  if (missingDomains.length > 0) {
    blockers.push(`Missing ${missingDomains.length} mandatory indicator domain(s).`);
  }
  if (zeroDenominatorDomains.length > 0) {
    blockers.push(`Zero denominator recorded for ${zeroDenominatorDomains.length} domain(s).`);
  }
  if (negativeValueDomains.length > 0) {
    blockers.push(`Negative values recorded for ${negativeValueDomains.length} domain(s).`);
  }
  if (missingBedDays) {
    blockers.push('Total bed days must be greater than zero.');
  }
  if (missingAihwReference) {
    blockers.push('Record the AIHW acknowledgement reference before marking as submitted.');
  }
  if (missingSubmittedAt) {
    blockers.push('Submitted records must retain the external submission timestamp.');
  }

  const blockerCount =
    missingDomains.length +
    zeroDenominatorDomains.length +
    negativeValueDomains.length +
    (missingBedDays ? 1 : 0) +
    (missingAihwReference ? 1 : 0) +
    (missingSubmittedAt ? 1 : 0);

  return {
    id: submission.id ?? null,
    facilityId: submission.facilityId ?? null,
    facilityName: submission.facilityName || 'Unknown facility',
    status,
    missingDomains,
    zeroDenominatorDomains,
    negativeValueDomains,
    missingBedDays,
    missingAihwReference,
    missingSubmittedAt,
    blockerCount,
    blockers,
    readiness: resolveReadiness(status, blockerCount),
  };
}

export function summariseGpmsSubmissionWorkflow(
  submissions: GpmsSubmissionLike[],
  expectedDomains: readonly string[],
): GpmsWorkflowSummary {
  const validations = submissions.map((submission) =>
    validateGpmsSubmission(submission, expectedDomains),
  );

  // Export gate: per AIHW QI Program Manual 4.0, the GPMS extract is the
  // artefact submitted to the regulator, so it is generated once internal
  // review reaches READY (preparation complete) or after external SUBMITTED
  // and any post-AIHW outcome state. DRAFT records remain blocked from export.
  const exportEligibleStates = new Set([
    'READY',
    'SUBMITTED',
    'ACCEPTED',
    'ACKNOWLEDGED',
    'REJECTED',
    'CLOSED',
  ]);

  return {
    totalSubmissions: validations.length,
    blockedCount: validations.filter((validation) => validation.readiness === 'blocked').length,
    draftCount: validations.filter((validation) => validation.readiness === 'draft').length,
    readyCount: validations.filter((validation) => validation.readiness === 'ready').length,
    submittedCount: validations.filter((validation) => validation.readiness === 'submitted').length,
    acceptedCount: validations.filter((validation) => validation.readiness === 'accepted').length,
    acknowledgedCount: validations.filter((validation) => validation.readiness === 'acknowledged')
      .length,
    rejectedCount: validations.filter((validation) => validation.readiness === 'rejected').length,
    closedCount: validations.filter((validation) => validation.readiness === 'closed').length,
    missingDomainCount: validations.reduce(
      (sum, validation) => sum + validation.missingDomains.length,
      0,
    ),
    missingReferenceCount: validations.filter((validation) => validation.missingAihwReference)
      .length,
    validations,
    canGenerateExport:
      validations.length > 0 &&
      validations.every(
        (validation) =>
          validation.blockerCount === 0 && exportEligibleStates.has(validation.status),
      ),
  };
}
